import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";

import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { Link } from "react-router-dom";
import { FaVolumeMute, FaVolumeUp } from "react-icons/fa";

interface NavbarProps {
  handleMenuClick: (page: string) => void;
}

interface VideoItem {
  id: number;
  judul: string;
  web_peduli: string;
  video: string;
  video_url: string;
}

const HeroUdin: React.FC<NavbarProps> = ({ handleMenuClick }) => {
  const [typedText, setTypedText] = useState("");
  const [charIndex, setCharIndex] = useState(0);
  const fullText = "#wargabantuwarga";

  const IconFaVolumeMute = FaVolumeMute as React.FC<React.SVGProps<SVGSVGElement>>;
  const IconFaVolumeUp = FaVolumeUp as React.FC<React.SVGProps<SVGSVGElement>>;

  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeSoundId, setActiveSoundId] = useState<number | null>(null);

  /* =======================
     FETCH VIDEO API
     ======================= */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/video?web_peduli=sumatera`)
      .then((res) => res.json())
      .then((res) => setVideos(res.data)) // ⬅️ ambil dari key `data`
      .catch((err) => console.error("Fetch video error:", err));
  }, []);

  /* =======================
     AUTO UNMUTE VIDEO TENGAH
     ======================= */
  useEffect(() => {
    const activeVideo = videos[activeIndex];
    if (activeVideo) {
      setActiveSoundId(activeVideo.id);
    }
  }, [activeIndex, videos]);

  /* =======================
     TYPING EFFECT
     ======================= */
  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (charIndex <= fullText.length) {
      timeout = setTimeout(() => {
        setTypedText(fullText.slice(0, charIndex));
        setCharIndex((prev) => prev + 1);
      }, 100);
    } else {
      timeout = setTimeout(() => {
        setCharIndex(0);
      }, 2000);
    }

    return () => clearTimeout(timeout);
  }, [charIndex]);

  return (
    <section className="relative w-full max-sm:h-[150vh] h-[100vh] overflow-hidden text-white -mt-20">
      {/* BACKGROUND */}
      <img className="absolute top-0 left-0 w-full h-full z-[-1] object-cover" src="/images/bgutama.jpg" alt="Background" />

      <div className="absolute inset-0 bg-black/75 z-0" />

      <div className="relative z-10 lg:flex items-center gap-8 h-full px-4 mt-40 lg:mt-6 mx-2 lg:mx-20">
        {/* LEFT */}
        <div className="w-full text-center lg:text-start">
          <div className="text-[45px] md:text-3xl xl:text-6xl font-bold">BERSAMA</div>

          <div className="text-[#f88434] font-bold text-[28px] md:text-3xl xl:text-[50px] max-sm:-mt-4">JADILAH BAGIAN DARI</div>

          <div className="text-[45px] md:text-3xl xl:text-7xl font-semibold font-bilbo max-sm:-mt-7 -mt-4">Peduli Sumatera</div>

          <p className="text-[#f88434] text-[18px] md:text-[27px] font-semibold h-[30px]">
            {typedText}
            <span className="animate-pulse">|</span>
          </p>

          <Link to="https://belaskasih.com/account/campaign/new" className="hidden lg:inline-block border-2 border-white rounded-lg px-6 py-2 my-6 text-lg font-semibold">
            Galang Dana Sekarang
          </Link>

          <p className="mb-6 text-sm text-gray-400 max-sm:text-center text-start hidden md:block ">
            Galang Dana di Peduli Sumatera merupakan program kerja sama dengan Platform BelasKasih.com agar perhimpunan kamu transparan, akuntable dan nyata.
          </p>
        </div>

        {/* RIGHT VIDEO SLIDER */}
        <div className="w-full mt-5 lg:mt-0 rounded-lg overflow-hidden">
          <Swiper
            modules={[Pagination, Autoplay]}
            pagination={{ clickable: true }}
            loop
            centeredSlides
            onSlideChange={(swiper: SwiperType) => setActiveIndex(swiper.realIndex)}
            breakpoints={{
              0: {
                slidesPerView: 1.2,
                spaceBetween: 3,
              },
              640: {
                slidesPerView: 1,
                spaceBetween: 10,
              },
            }}
            className="w-full">
            {videos.map((video, index) => {
              const isActive = index === activeIndex;

              return (
                <SwiperSlide key={video.id}>
                  <div className={`relative transition-all duration-500 ${isActive ? "scale-100" : "scale-90 opacity-60"}`}>
                    <video
                      src={video.video_url}
                      autoPlay
                      muted={activeSoundId !== video.id}
                      loop
                      playsInline
                      controls={false}
                      onClick={() => setActiveSoundId(activeSoundId === video.id ? null : video.id)}
                      className="w-full max-sm:h-[480px] md:h-[500px] lg:h-[320px] 2xl:h-[450px] object-cover rounded-lg"
                    />

                    {/* SOUND TOGGLE */}
                    <button onClick={() => setActiveSoundId(activeSoundId === video.id ? null : video.id)} className="absolute bottom-3 right-3 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition text-[18px]">
                      {activeSoundId === video.id ? <IconFaVolumeUp /> : <IconFaVolumeMute />}
                    </button>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
          {/* MOBILE CTA */}
          <p className=" mt-3 mb-6 text-xs text-gray-400 max-sm:text-center text-start block md:hidden ">
            Galang Dana di Peduli Sumatera merupakan program kerja sama dengan Platform BelasKasih.com agar perhimpunan kamu transparan, akuntable dan nyata.
          </p>
          <div className="block md:hidden">
            <div className="mt-3 text-sm text-gray-200 text-center">Diikuti Oleh :</div>
            <div className="flex justify-center gap-2 items-center flex-wrap mt-2">
              <img src="/images/bk.png" alt="" className="w-9 h-10" />
              <img src="/images/inh.png" alt="" className="w-10 h-9" />
              <img src="/images/kr.png" alt="" className="w-10 h-10" />
              <img src="/images/knpi.png" alt="" className="w-9 h-10" />
              <img src="/images/pragib.png" alt="" className="w-10 h-10" />
              <img src="/images/gerakin.png" alt="" className="w-13 h-8" />
            </div>
            <div className="mt-3 text-center  text-sm text-gray-200">Mari Bergabung Bersama Kami</div>
          </div>
          <div className="flex justify-center  mt-6 lg:hidden">
            <Link to="https://belaskasih.com/account/campaign/new" className="border-2 border-white rounded-lg px-5 py-2 text-base font-semibold">
              Galang Dana Sekarang
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroUdin;
